import { ExtendedRecordMap } from "notion-types";

/**
 * Notion is rolling out a new response shape for its unofficial API: records
 * arrive as `{ value: { value: <record>, role } }` instead of `{ value: <record> }`.
 *
 * react-notion-x still reads `entry.value`, so under the new shape every field it
 * wants is one level too deep and reads `undefined` — which surfaces as
 *
 *     TypeError: Cannot read properties of undefined (reading 'replace')
 *         at Block (react-notion-x)
 *
 * and takes the whole app down through the error boundary.
 *
 * This unwraps the nested shape back to the flat one at the point the recordMap
 * enters the app, so no downstream code has to change.
 *
 * Both shapes can appear in the *same* payload while the rollout is in progress,
 * so every entry is inspected individually rather than assuming the response is
 * uniformly old or new.
 */

/**
 * Record tables whose entries are `{ value, role }` wrappers.
 *
 * Every table react-notion-x's `ExtendedRecordMap` knows about, plus the ones
 * Notion has added since. Unwrapping is unconditional-if-present: a table that
 * is absent from the payload is left absent, never created.
 */
const RECORD_TABLES = [
  "block",
  "collection",
  "collection_view",
  "notion_user",
  "space",
  "team",
  "comment",
  "discussion",
  "block_activity",
  "user_root",
  "user_settings",
  "collection_view_page",
] as const;

interface RecordEntry {
  value?: unknown;
  role?: unknown;
  [key: string]: unknown;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Is `inner` the new API's wrapper (`{ value, role }`) rather than the record
 * itself? Every real Notion record carries an `id`; a wrapper never does, and
 * always carries `role` and/or `value`. That asymmetry is what we key off.
 */
const isWrapper = (inner: Record<string, unknown>): boolean =>
  !("id" in inner) && ("role" in inner || "value" in inner);

/**
 * `{ value: { value: X, role } }` -> `{ value: X, role }`.
 *
 * A flat entry (`{ value: X }` where X is the record) is returned untouched.
 *
 * The case that matters most: a block you have no access to comes back as
 *
 *     { value: { role: "none" } }
 *
 * with no record inside. Left alone, `entry.value` is the truthy `{role:"none"}`,
 * so react-notion-x does NOT treat it as missing — it renders it as a block, then
 * dies reading `block.type` ("Cannot read properties of undefined (reading
 * 'replace')"). Collapsing it to `value: undefined` makes react-notion-x log
 * "missing block" and skip it, which is the behaviour it already handles.
 */
const unwrapEntry = (entry: unknown): unknown => {
  if (!isPlainObject(entry)) {
    return entry;
  }

  const inner = (entry as RecordEntry).value;

  if (isPlainObject(inner) && isWrapper(inner)) {
    return {
      ...entry,
      // undefined when the wrapper carries no record (access denied)
      value: inner.value,
      role: (entry as RecordEntry).role ?? inner.role,
    };
  }

  return entry;
};

/**
 * ── Block-value repairs ──────────────────────────────────────────────────────
 *
 * Beyond the wrapper unwrapping above, two newer recordMap shapes crash or lose
 * content in react-notion-x 6.x. Both are fixed here, on the block record, so no
 * downstream code (renderer or mindmap tree builder) has to know about them.
 */

/**
 * Notion now emits a 4th heading level, `header_4` (and, defensively, deeper).
 * react-notion-x has no case for it, logs "Unsupported type header_4" and drops
 * the heading; the mindmap tree builder's HEADING_LEVEL map drops it too. We
 * downgrade it to the deepest heading both DO understand, `sub_sub_header` (h3).
 *
 * This is a deliberate fidelity loss (h4 → h3): the heading renders one level
 * too shallow and, in the mindmap, nests as an h3 rather than a distinct h4.
 * That is strictly better than the heading vanishing entirely.
 */
const HEADING_DOWNGRADE: Record<string, string> = {
  header_4: "sub_sub_header",
  header_5: "sub_sub_header",
  header_6: "sub_sub_header",
};

/**
 * Asset-family block types. react-notion-x's Asset/AssetWrapper resolve a URL
 * from `properties.source[0][0]` (and, for embeds, `format.display_source`) and
 * call string methods on it — `.split` (tweet), `.indexOf` (video/embed),
 * `.startsWith`/`.includes` (image). A non-string there throws
 * "e2.split is not a function" out of AssetWrapper and takes the app down.
 */
const ASSET_TYPES = new Set([
  "video",
  "image",
  "embed",
  "pdf",
  "figma",
  "maps",
  "drive",
  "gist",
  "codepen",
  "tweet",
  "audio",
  "file",
  "typeform",
  "excalidraw",
]);

/**
 * Best-effort recovery of a URL string from a value Notion wrapped: a bare
 * string, an `{ url }`/`{ href }`/`{ signed_url }` object, or a nested array
 * (`["url"]`, `[["url"]]`). Returns null when nothing string-like is in reach —
 * the caller then strips the field so react-notion-x's own `!source` guard runs.
 */
const coerceToUrlString = (v: unknown): string | null => {
  if (typeof v === "string") {
    return v;
  }
  if (Array.isArray(v)) {
    for (const el of v) {
      const s = coerceToUrlString(el);
      if (s !== null) {
        return s;
      }
    }
    return null;
  }
  if (isPlainObject(v)) {
    for (const key of ["url", "href", "signed_url", "signedUrl"]) {
      if (typeof v[key] === "string") {
        return v[key] as string;
      }
    }
  }
  return null;
};

/**
 * Ensure an asset block's URL fields are strings. Coerces an obviously-wrapped
 * URL; otherwise strips the field so the renderer skips the asset instead of
 * crashing. Returns the same object when nothing needed fixing.
 */
const guardAssetSource = (
  value: Record<string, unknown>,
): Record<string, unknown> => {
  let out = value;

  const properties = out.properties;
  if (isPlainObject(properties) && Array.isArray(properties.source)) {
    const raw = (properties.source[0] as unknown[] | undefined)?.[0];
    if (raw !== undefined && typeof raw !== "string") {
      const coerced = coerceToUrlString(raw);
      const nextProperties = { ...properties };
      if (coerced !== null) {
        nextProperties.source = [[coerced]];
      } else {
        delete nextProperties.source;
      }
      out = { ...out, properties: nextProperties };
    }
  }

  const format = out.format;
  if (isPlainObject(format) && "display_source" in format) {
    const ds = format.display_source;
    if (ds !== undefined && ds !== null && typeof ds !== "string") {
      const coerced = coerceToUrlString(ds);
      const nextFormat = { ...format };
      if (coerced !== null) {
        nextFormat.display_source = coerced;
      } else {
        delete nextFormat.display_source;
      }
      out = { ...out, format: nextFormat };
    }
  }

  return out;
};

/**
 * A Notion rich-text property is a list of decorations, each `[text, formats?]`
 * — so `properties.caption[0][0]` is a string. Some blocks arrive with the list
 * wrapped one level too deep (`[[["text"]]]` instead of `[["text"]]`), which is
 * what breaks an image with a caption: react-notion-x's AssetWrapper hands
 * `caption[0][0]` to `parsePageId`, gets an array, and dies with
 *
 *     TypeError: e2.split is not a function
 *         at AssetWrapper (react-notion-x)
 *
 * A decoration always starts with a string, so peeling while the head is still
 * an array restores the expected depth whatever the nesting.
 */
const MAX_DECORATION_UNWRAP = 4;

const unwrapDecoration = (decoration: unknown): unknown => {
  let out = decoration;
  for (let i = 0; i < MAX_DECORATION_UNWRAP; i++) {
    if (!Array.isArray(out) || !Array.isArray(out[0])) {
      return out;
    }
    out = out[0];
  }
  return out;
};

/** Repair over-nested rich-text properties on a block value. */
const guardProperties = (
  value: Record<string, unknown>,
): Record<string, unknown> => {
  const properties = value.properties;
  if (!isPlainObject(properties)) {
    return value;
  }

  let nextProperties: Record<string, unknown> | null = null;
  for (const key of Object.keys(properties)) {
    const list = properties[key];
    if (!Array.isArray(list)) {
      continue;
    }
    const unwrapped = list.map(unwrapDecoration);
    if (unwrapped.some((decoration, i) => decoration !== list[i])) {
      nextProperties = nextProperties ?? { ...properties };
      nextProperties[key] = unwrapped;
    }
  }

  return nextProperties ? { ...value, properties: nextProperties } : value;
};

/** Apply the block-value repairs above. Returns the same object if unchanged. */
const normalizeBlockValue = (
  value: Record<string, unknown>,
): Record<string, unknown> => {
  let out = value;

  const type = out.type;
  // hasOwn, not `in`: `in` also matches inherited keys ("toString", "__proto__")
  if (typeof type === "string" && Object.hasOwn(HEADING_DOWNGRADE, type)) {
    out = { ...out, type: HEADING_DOWNGRADE[type] };
  }

  // Before guardAssetSource: it reads `properties.source[0][0]` and an
  // over-nested source would otherwise look like a non-string to strip.
  out = guardProperties(out);

  if (typeof out.type === "string" && ASSET_TYPES.has(out.type)) {
    out = guardAssetSource(out);
  }

  return out;
};

/** Run {@link normalizeBlockValue} over an already-unwrapped block entry. */
const normalizeBlockEntry = (entry: unknown): unknown => {
  if (!isPlainObject(entry)) {
    return entry;
  }
  const value = (entry as RecordEntry).value;
  if (!isPlainObject(value)) {
    return entry;
  }
  const nextValue = normalizeBlockValue(value);
  return nextValue === value ? entry : { ...entry, value: nextValue };
};

const normalizeTable = (table: unknown, isBlockTable: boolean): unknown => {
  if (!isPlainObject(table)) {
    return table;
  }
  // Object.create(null): record ids come straight off the wire, and a literal
  // `{}` would let an id of "__proto__" write the prototype instead of a key —
  // poisoning Object.prototype and silently dropping the record.
  const acc: Record<string, unknown> = Object.create(null);
  for (const id of Object.keys(table)) {
    const unwrapped = unwrapEntry(table[id]);
    acc[id] = isBlockTable ? normalizeBlockEntry(unwrapped) : unwrapped;
  }
  // Spread back onto a normal object: it copies `__proto__` as a plain key
  // (unlike assignment) while restoring the prototype react-notion-x may expect.
  return { ...acc };
};

/**
 * Returns a recordMap whose record entries all use the flat `.value` shape.
 *
 * Does not mutate the input. Tables that are absent are left absent, and tables
 * that are not `{ value }` maps are passed through untouched — `collection_query`
 * (keyed collection -> view -> result), `signed_urls` and `preview_images`.
 */
export const normalizeRecordMap = <T>(recordMap: T): T => {
  if (!isPlainObject(recordMap)) {
    return recordMap;
  }

  const out: Record<string, unknown> = { ...recordMap };

  for (const table of RECORD_TABLES) {
    if (table in out && out[table] !== undefined) {
      out[table] = normalizeTable(out[table], table === "block");
    }
  }

  return out as T;
};

export type { ExtendedRecordMap };
